import { Box, CircularProgress, Divider, IconButton, Pagination, Typography } from '@mui/material';
import CustomDataGrid2 from '@src/components/CustomDataGrid/CustomDataGrid2';
import { DeleteIcon, LockIcon } from '@src/components/Icons';
import CustomPermission from '@src/components/Permissions/CustomPermission';
import SearchBar from '@src/components/SearchBar';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import usePermission from '@src/hooks/usePermission';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CustomAlertType } from '..';
import { ProductCatalog } from '@src/pages/Products';
import { Filters, Option } from '@src/interfaces/type';
import { setPageNumber } from '@src/store/reducers/catalogReducer';
import { useParams } from 'react-router-dom';
import CustomButton from '@src/components/CustomButton/CustomButton';
import ProductCard from '@src/components/CustomCard/ProductCard';
import CustomFilter from '@src/components/CustomFilter/CustomFilter';
import Loading from '@src/components/Loading/Loading';
import CustomTextButton from '@src/components/CustomButton/CustomTextButton';
import ProductList from '@src/components/ProductListing/ProductList';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: 100%;
	height: auto;
	@media screen and (max-width: 600px) {
	}
`;

const StyledDataGridHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	@media screen and (max-width: 750px) {
		gap: 15px;
		flex-direction: column;
		margin-top: 15px;
	}
`;

const SelectedItemsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-right: 6px;
	gap: 5px;
	font-size: 16px;
	font-weight: 750;
	line-height: 21.82px;
	transition: width 0.3s ease;
`;

type ProductsListingProps = {
	handleRowClick: any;
	onRowsSelectionHandler: any;
	categoryOptions: Option[];
	rows: any;
	columns: any;
	style?: React.CSSProperties;
	page: number;
	handleChange: any;
	totalPages: number;
	setCategoryId: (data: string) => void;
	setPage: (data: number) => void;
	searchValue: string;
	setSearchValue: (data: string) => void;
	searching: boolean;
	setSearching: (data: boolean) => void;
	selectedItemIds: string[];
	loading?: boolean;
	addProduct?: any;
	controlRowSelection?: boolean;
	productCount: number;
	filters: Filters | null;
	setFilters: (data: Filters) => void;
	handleSpecificProductSelect: any;
	showDetails: any;
	productContainerHeight?: any;
	loadingProducts: boolean;
	setLoadingProducts: (data: boolean) => void;
	handleEditClick?: any;
	handleUpdate?: any;
	handleEditClose?: any;
	StyledTextFieldCustomerPrice?: any;
	handleCustomerPrice?: any;
};

const PaginatedProductData: React.FC<ProductsListingProps> = ({
	handleRowClick,
	onRowsSelectionHandler,
	categoryOptions,
	rows,
	columns,
	page,
	handleChange,
	totalPages,
	setCategoryId,
	setPage,
	searchValue,
	setSearchValue,
	searching,
	setSearching,
	selectedItemIds,
	loading,
	addProduct,
	controlRowSelection = false,
	productCount,
	style = {
		transition: 'height 0.3s ease',
		maxHeight: 'calc(100vh - 250px)',
		minHeight: 'calc(100vh - 250px)',
	},
	filters,
	setFilters,
	handleSpecificProductSelect,
	showDetails,
	productContainerHeight = 'auto',
	loadingProducts,
	setLoadingProducts,
	handleEditClick,
	handleUpdate,
	handleEditClose,
	StyledTextFieldCustomerPrice,
	handleCustomerPrice,
}) => {
	const [deleteProductModal, setDeleteProductModal] = useState(false);
	const pageNumber: any = useAppSelector((state) => state?.supplierCatalog?.pageNumber);
	const dispatch = useAppDispatch();

	const { id } = useParams();

	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchValue(value);
		setPage(1);
		dispatch(setPageNumber(1));
	};

	const checkPermission = usePermission();
	const canCreateProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.CREATE, id);
	const canViewProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.VIEW, id);
	const canUploadCSV = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.CREATE_CSV, id);
	const canDeleteProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.DELETE, id);

	const loadMore = () => {
		setLoadingProducts(true);
		//@ts-ignore
		setPage((prevState: number) => prevState + 1);
	};

	//Reset state on unmount
	useEffect(() => {
		return () => {
			setPage(1);
		};
	}, []);

	return (
		<StyledContainer>
			{canViewProduct ? (
				<>
					<StyledDataGridHeader>
						<div id="gridHeader">
							<SearchBar
								style={{
									width: '328px',
									height: '36px',
								}}
								value={searchValue}
								onChange={handleSearch}
								isLoading={false}
							/>
						</div>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: '12px',
							}}
						>
							<SelectedItemsContainer>
								<Typography>
									{selectedItemIds?.length
										? `${selectedItemIds.length} selected out of ${productCount}`
										: `Total Items: ${productCount}`}
								</Typography>
							</SelectedItemsContainer>
							{addProduct ? (
								<CustomButton
									title={
										loading ? (
											<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
										) : (
											'Add'
										)
									}
									handleClick={addProduct}
									heightVariant="medium"
									disabled={!selectedItemIds?.length || loading}
									styles={{
										width: '116px',
										height: '36px',
										borderRadius: '4px',
										padding: '7px 12px',
									}}
								/>
							) : null}
						</div>
					</StyledDataGridHeader>
					<Box>
						<CustomFilter
							filters={filters}
							setFilters={setFilters}
							setPage={setPage}
							setLoading={setSearching}
							filterType="server"
						/>
						<Divider />

						<ProductList
							//@ts-ignore
							products={rows}
							searching={searching}
							loadingProducts={loadingProducts}
							loadMore={loadMore}
							handleSpecificProductSelect={handleSpecificProductSelect}
							showDetails={showDetails}
							style={{ overflow: 'auto', height: productContainerHeight }}
							totalCount={productCount}
							handleEditClick={handleEditClick}
							handleUpdate={handleUpdate}
							handleEditClose={handleEditClose}
							StyledTextFieldCustomerPrice={StyledTextFieldCustomerPrice}
							handleCustomerPrice={handleCustomerPrice}
						/>
					</Box>
				</>
			) : (
				<CustomPermission
					icon={<LockIcon />}
					title="We are sorry..."
					paragraph="You do not have permission to view products. Contact 
				an administrator to get permission."
				/>
			)}
		</StyledContainer>
	);
};

export default PaginatedProductData;
